import React, { useEffect, useState } from 'react';
import AsyncSelect from 'react-select/async';
import { cachedFetch } from '../utils/cached-api-wrapper';
import Utils from '../utils/utils';

// Shared GL-segment picker for the option-backed segments (GL Section, Practice
// Group). Used by both the invoice header (invoice.js) and the per-line editor
// (additional-fields-modal.js) so the two render identically.

// Maps a stored value to a react-select option. Accepts the `{code, name}` object
// a user pick persists, a freshly-loaded `{Id, Description}` API row, and a bare
// code string — the shape the engine/api infer with no name (GLNatural-derived
// section, header->line cascade), which would otherwise render as a blank picker.
export const codeOptionMap = (stored) => {
    if (stored === null || stored === undefined) return null;
    const isScalar = typeof stored === 'string' || typeof stored === 'number';
    if (!isScalar && typeof stored !== 'object') return null;
    const rawCode = isScalar ? stored : (stored.code ?? stored.Id ?? stored.value ?? stored.GLValue);
    // Normalise scalar and object codes the same way (trimmed string) so the
    // rendered value and the resolver's String()-based match stay consistent.
    const code = (rawCode === null || rawCode === undefined || rawCode === '') ? null : String(rawCode).trim();
    const description = isScalar ? undefined : (stored.name ?? stored.Description);
    if (!code) return null;
    return {
        value: { code, name: description },
        label: (
            <>
                <strong>{code}</strong>{description ? ` - ${description}` : ''}
            </>
        ),
    };
};

const makeClientOptionsLoader = (fieldName) => (search_query = '', callback = () => {}) => {
    const val = (search_query || '').trim().replace(/\//g, '');
    const filters = val.length > 0 ? [{ id: 'search', value: val }] : [];
    const searchData = `?field_name=${fieldName}&filters=${encodeURIComponent(JSON.stringify(filters))}`;
    cachedFetch(`/client/options/${searchData}`, 'GET', {}, (response) => {
        // An error envelope comes back as an object, not a list; treat anything that
        // is not a list as "no options" rather than throwing out of the render.
        const data = Array.isArray(response?.data) ? response.data : [];
        const rows = data.map(codeOptionMap).filter(Boolean);
        callback(rows.slice(0, 10));
    });
};

// The /client/options field_name differs from the invoice-json segment key
// (Practice Group is queried as `PracticeGroup`, not `GLPracticeGroup`).
const SEGMENT_LOADERS = {
    GLSection: makeClientOptionsLoader('GLSection'),
    GLPracticeGroup: makeClientOptionsLoader('PracticeGroup'),
    GLTimekeeper: makeClientOptionsLoader('GLTimekeeper'),
    GLOffice: makeClientOptionsLoader('GLOffice'),
};

// A segment can arrive as a bare code string (engine/api inferred it with no
// name). When it does, look the code up in the field's options so the picker
// shows "code - name" like a user pick; fall back to code-only (codeOptionMap)
// if nothing matches. The lookup is a code-filtered fetch of its own (not the
// AsyncSelect's default list, which is capped at 10 rows and may not contain the
// inferred code) — correctness over reusing that request.
export const useResolvedSegmentOption = (stored, loader) => {
    const [resolved, setResolved] = useState(null);
    // A bare code can arrive as a string or (defensively) a number; normalise and
    // trim so a whitespace-only value is treated as empty, not a real code.
    const bareCode = (typeof stored === 'string' || typeof stored === 'number')
        ? (String(stored).trim() || null) : null;
    useEffect(() => {
        if (!bareCode || !loader) {
            setResolved(null);
            return undefined;
        }
        let cancelled = false;
        loader(bareCode, (rows) => {
            if (cancelled) return;
            setResolved((rows || []).find((r) => String(r?.value?.code) === bareCode) ?? null);
        });
        return () => { cancelled = true; };
    }, [bareCode, loader]);
    if (!bareCode) return codeOptionMap(stored);
    // Guard against a stale `resolved` from a previous code (line switch): only
    // use it when it matches the current code, else fall back until the fetch lands.
    const fresh = String(resolved?.value?.code) === bareCode ? resolved : null;
    return fresh ?? codeOptionMap(stored);
};

// `onChange` receives the persisted shape directly: the `{code, name}` object on
// a pick, or `undefined` when cleared — the same value the backend's
// _unwrap_segment_code already tolerates. Callers wire it to their own
// updateInvoice signature (header vs. per-line).
const GlSegmentSelect = ({ segment, value, onChange, isDisabled, name, placeholder, ariaLabel }) => {
    const loader = SEGMENT_LOADERS[segment];
    const option = useResolvedSegmentOption(value, loader);
    if (!loader) return null; // unknown segment — nothing to load against
    return (
        <AsyncSelect
            styles={Utils.customStyleSelect}
            menuPortalTarget={document.body}
            value={option}
            onChange={(choice) => onChange(choice ? choice.value : undefined)}
            classNamePrefix="select"
            isDisabled={isDisabled}
            name={name}
            // A placeholder is not an accessible name: a mount inside a labelled
            // table cell passes the column's label so the control is still named.
            aria-label={ariaLabel}
            placeholder={placeholder}
            isClearable
            defaultOptions
            cacheOptions
            loadOptions={loader}
        />
    );
};

export default GlSegmentSelect;
